import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import HttpAPICall from '../../services/HttpAPICall';
import Loader from '../../components/ui/Loader/Loader';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const startIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-2x-green.png',
  iconSize: [35, 35],
  iconAnchor: [18, 35],
  popupAnchor: [0, -35]
});

const endIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-2x-red.png',
  iconSize: [35, 35],
  iconAnchor: [18, 35],
  popupAnchor: [0, -35]
});

const carIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/kml/shapes/cabs.png',
  iconSize: [42, 25],
  iconAnchor: [21, 12],
  popupAnchor: [0, -12]
});

const FitBounds = ({ points }) => {
  const map = useMap();
  const hasFittedRef = useRef(false);

  useEffect(() => {
    if (points && points.length > 0 && !hasFittedRef.current) {
      const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [80, 80], maxZoom: 17 });
      hasFittedRef.current = true;
    }
  }, [map, points]);

  return null;
};

const CarMarker = ({ points }) => {
  const [position, setPosition] = useState(null);

  const animationRef = useRef(null);
  const segmentRef = useRef(0);
  const progressRef = useRef(0);
  const lastTimeRef = useRef(null);

  const SPEED = 0.00002;

  useEffect(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (!points || points.length === 0) {
      setPosition(null);
      return;
    }

    if (points.length === 1) {
      setPosition(points[0]);
      return;
    }

    segmentRef.current = 0;
    progressRef.current = 0;
    lastTimeRef.current = null;

    setPosition(points[0]);

    const animate = (timestamp) => {
      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
      }

      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      const currentIndex = segmentRef.current;
      const nextIndex = currentIndex + 1;

      if (nextIndex >= points.length) {
        setPosition(points[points.length - 1]);

        animationRef.current = null;
        return;
      }

      const start = points[currentIndex];
      const end = points[nextIndex];

      const latDiff = end.lat - start.lat;
      const lngDiff = end.lng - start.lng;

      const distance = Math.sqrt(
        latDiff * latDiff + lngDiff * lngDiff
      );

      if (distance === 0) {
        segmentRef.current = nextIndex;

        animationRef.current =
          requestAnimationFrame(animate);

        return;
      }

      progressRef.current +=
        (deltaTime * SPEED) / distance;

      if (progressRef.current >= 1) {
        segmentRef.current = nextIndex;
        progressRef.current = 0;

        setPosition(points[nextIndex]);

        if (nextIndex === points.length - 1) {
          animationRef.current = null;
          return;
        }
      } else {
        const lat =
          start.lat +
          (end.lat - start.lat) *
          progressRef.current;

        const lng =
          start.lng +
          (end.lng - start.lng) *
          progressRef.current;

        setPosition({
          lat,
          lng
        });
      }

      animationRef.current =
        requestAnimationFrame(animate);
    };

    animationRef.current =
      requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }

      lastTimeRef.current = null;
    };
  }, [points]);

  if (!position) {
    return null;
  }

  return (
    <Marker
      position={[position.lat, position.lng]}
      icon={carIcon}
    />
  );
};
const TrackingMap = ({ deviceData, mapLoading, setMapLoading, mapError, setMapError }) => {
  const [points, setPoints] = useState([]);
  const [startPoint, setStartPoint] = useState(null);
  const [endPoint, setEndPoint] = useState(null);
  const [mapKey, setMapKey] = useState(0);

  const getPathPoints = () => {
    if (!deviceData) return [];

    const dataArray = Array.isArray(deviceData)
      ? deviceData
      : (deviceData.data || deviceData.result || deviceData.records || []);

    const points = dataArray
      .filter(item => {
        const lat = parseFloat(item.latitude);
        const lng = parseFloat(item.longitude);
        return Number.isFinite(lat) && Number.isFinite(lng);
      })
      .map(item => ({
        lat: parseFloat(item.latitude),
        lng: parseFloat(item.longitude)
      }));

    return points;
  };


  useEffect(() => {
    if (!deviceData) return;

    setMapLoading(true);
    setMapError(null);

    const pts = getPathPoints();

    if (!pts.length) {
      setMapError('No valid location data found');
      setMapLoading(false);
      return;
    }

    setPoints(pts);
    setStartPoint(pts[0]);
    setEndPoint(pts[pts.length - 1]);
    setMapKey(prev => prev + 1);

    // No separate API call
    setMapLoading(false);
  }, [deviceData]);

  const renderAddress = (location, type) => {
    const area = location?.area || location?.locality || location?.subLocality || location?.village || '-';
    const city = location?.city || location?.district || '-';
    const state = location?.state || '-';
    const pincode = location?.pincode || '-';
    const address = location?.formatted_address || 'Location not available';

    return (
      <div>
        <div><b>Area:</b> {area}</div>
        <div><b>City:</b> {city}</div>
        <div><b>State:</b> {state}</div>
        <div><b>Pincode:</b> {pincode}</div>
        <div><b>Address:</b><br />{address}</div>
      </div>
    );
  };

  if (mapLoading) {
    return (
      <div className="map-loading-container">
        <Loader />
        <p className="mt-2 mb-0" style={{ fontSize: '14px' }}>Loading map...</p>
      </div>
    );
  }

  if (mapError) {
    return (
      <div className="map-error-container text-center mt-5">
        <h5>Map Error</h5>
        <p>{mapError}</p>
      </div>
    );
  }

  if (!points.length) {
    return (
      <div className="map-error-container text-center mt-5">
        <p>No location data available</p>
      </div>
    );
  }

  return (
    <MapContainer
      key={mapKey}
      center={[startPoint.lat, startPoint.lng]}
      zoom={15}
      style={{ height: '100%', width: '100%' }}
      zoomControl={true}
      zoomAnimation={true}
      fadeAnimation={true}
      markerZoomAnimation={true}
    >
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">TappetBox</a>' />

      <FitBounds points={points} />

      {/* Polyline Route */}
      <Polyline
        positions={points.map(p => [p.lat, p.lng])}
        color="#1677ff"
        weight={6}
        opacity={1}
      />
      {startPoint && (
        <Marker
          position={[startPoint.lat, startPoint.lng]}
          icon={startIcon}
        >
          <Popup maxWidth={280}>
            <div style={{ minWidth: '220px', padding: '8px' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  marginBottom: '10px',
                  color: '#4caf50'
                }}
              >
                🟢 Start Point
              </div>

              <div>
                <b>Latitude:</b> {startPoint.lat.toFixed(6)}
              </div>

              <div>
                <b>Longitude:</b> {startPoint.lng.toFixed(6)}
              </div>
            </div>
          </Popup>
        </Marker>
      )}

      {endPoint && (
        <Marker
          position={[endPoint.lat, endPoint.lng]}
          icon={endIcon}
        >
          <Popup maxWidth={280}>
            <div style={{ minWidth: '220px', padding: '8px' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  marginBottom: '10px',
                  color: '#ef2929'
                }}
              >
                🔴 End Point
              </div>

              <div>
                <b>Latitude:</b> {endPoint.lat.toFixed(6)}
              </div>

              <div>
                <b>Longitude:</b> {endPoint.lng.toFixed(6)}
              </div>
            </div>
          </Popup>
        </Marker>
      )}

      {/* Car Marker with Animation */}
      <CarMarker points={points} />

      {/* Legend */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        zIndex: 1000,
        background: '#fff',
        borderRadius: '10px',
        padding: '16px 18px',
        boxShadow: '0 2px 10px rgba(0,0,0,.18)',
        minWidth: '150px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
          <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#4caf50', border: '3px solid white', boxShadow: '0 0 0 2px #4caf50', marginRight: '12px' }} />
          <span style={{ fontSize: '14px', color: '#222' }}>Start Point</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
          <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#ef2929', border: '3px solid white', boxShadow: '0 0 0 2px #ef2929', marginRight: '12px' }} />
          <span style={{ fontSize: '14px', color: '#222' }}>End Point</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
          <span style={{ width: '22px', height: '4px', background: '#1677ff', marginRight: '12px', display: 'inline-block' }} />
          <span style={{ fontSize: '14px', color: '#222' }}>Route</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '21px', width: '22px', marginRight: '12px', textAlign: 'center' }}>🚗</span>
          <span style={{ fontSize: '14px', color: '#222' }}>Moving Vehicle</span>
        </div>
      </div>
    </MapContainer>
  );
};

const AssetTracking = (props) => {
  const access_token = useSelector(state => state.auth.access_token);
  const [imei, setImei] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [deviceData, setDeviceData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const firstLoad = useRef(false);

  useEffect(() => {
    const now = new Date();
    const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000);
    setStartDate(twelveHoursAgo);
    setEndDate(now);
  }, []);

  useEffect(() => {
    if (props?.deviceData?.imei_no) {
      setImei(props.deviceData.imei_no);
    }
  }, [props?.deviceData?.imei_no]);

  // useEffect(() => {
  //   if (!firstLoad.current && imei && startDate && endDate) {
  //     firstLoad.current = true;
  //     fetchDeviceData(imei, startDate, endDate);
  //   }
  // }, [imei, startDate, endDate]);

  const fetchDeviceData = (selectedImei, selectedStartDate, selectedEndDate) => {
    setLoading(true);
    setDeviceData(null);
    setMapError(null);
    setMapLoading(false);

    const requestBody = {
      imeIs: [selectedImei.trim()],
      startDate: new Date(selectedStartDate).toISOString(),
      endDate: new Date(selectedEndDate).toISOString()
    };

    HttpAPICall.withAthorization(
      'POST',
      'https://api6.tappetbox.in/api/DeviceData/get-historical',
      access_token,
      {},
      requestBody,
      (resp) => {
        setDeviceData(resp.data);
        toast.success('Device data fetched successfully', {
          position: toast.POSITION.TOP_RIGHT
        });
      },
      (error) => {
        console.error('Device history error:', error);
        toast.error('Failed to fetch device data', {
          position: toast.POSITION.TOP_RIGHT
        });
      }
    ).finally(() => {
      setLoading(false);
    });
  };

  const handleDateTimeChange = (field, value) => {
    if (field === 'startDate') setStartDate(value);
    if (field === 'endDate') setEndDate(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!imei) {
      toast.error('Please select an IMEI number', {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates', {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be greater than end date', {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }

    fetchDeviceData(imei, startDate, endDate);
  };

  return (
    <div className="page_containt row pl2">
      <div className="pageTbl col-12 pl4">
        <div className="bg-white p-3">
          {props?.deviceData?.asset && (
            <table className="table table-hover table-borderless mt-2">
              <tbody>
                <tr>
                  <td className="details-label" style={{ width: '34%' }}>Asset</td>
                  <td className="details-name" style={{ width: '66%' }}>
                    {props?.deviceData?.asset?.asset_name || '-'}
                    ({props?.deviceData?.asset?.asset_nickname || '-'})
                  </td>
                </tr>
                <tr>
                  <td className="details-label" style={{ width: '34%' }}>Group</td>
                  <td className="details-name" style={{ width: '66%' }}>
                    {props?.deviceData?.group?.group_name || '-'}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row align-items-center my-3">
              <div className="col-sm-4">
                <label htmlFor="imei" className="form-label require">IMEI</label>
              </div>
              <div className="col-sm-4">
                <input
                  type="text"
                  id="imei"
                  className="form-control"
                  value={imei || ''}
                  readOnly
                  disabled
                  placeholder="IMEI Number"
                />
              </div>
            </div>

            <div className="row align-items-center my-3">
              <div className="col-sm-4">
                <label htmlFor="startDate" className="form-label require">Start Date</label>
              </div>
              <div className="col-sm-4">
                <DatePicker
                  selected={startDate}
                  onChange={value => handleDateTimeChange('startDate', value)}
                  dateFormat="dd-MMM-yyyy HH:mm"
                  className="form-control"
                  showMonthDropdown
                  showYearDropdown
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                  autoComplete="off"
                  scrollMonthYearDropdown
                  required
                  placeholderText="Please Enter Start Date & Time"
                />
              </div>
            </div>

            <div className="row align-items-center my-3">
              <div className="col-sm-4">
                <label htmlFor="endDate" className="form-label require">End Date</label>
              </div>
              <div className="col-sm-4">
                <DatePicker
                  selected={endDate}
                  onChange={value => handleDateTimeChange('endDate', value)}
                  dateFormat="dd-MMM-yyyy HH:mm"
                  className="form-control"
                  showMonthDropdown
                  showYearDropdown
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                  autoComplete="off"
                  scrollMonthYearDropdown
                  required
                  placeholderText="Please Enter End Date & Time"
                />
              </div>
            </div>

            <div className="row align-items-center my-3">
              <div className="col-sm-7"></div>
              <div className="col-sm-2">
                <button type="submit" disabled={loading} className="btn btn-primary">
                  GO
                  {loading && (
                    <span className="ms-2">
                      <i className="fa fa-refresh fa-spin"></i>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>

          {deviceData && !loading && (
            <div
              className="tracking-map-wrapper mb-5"
              style={{
                position: 'relative',
                width: '100%',
                height: '500px',
                marginTop: '20px',
                overflow: 'hidden',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                background: '#f5f5f5'
              }}
            >
              <TrackingMap
                deviceData={deviceData}
                mapLoading={mapLoading}
                setMapLoading={setMapLoading}
                mapError={mapError}
                setMapError={setMapError}
              />
            </div>
          )}

          {loading && <Loader />}
        </div>
      </div>
    </div>
  );
};

export default AssetTracking;